import { pageIntroContent } from '@insify/css/classes.module.css';
import { textXs } from '@insify/design-system/typography';
import { RCHeading, RCParagraph, RichContentWithTooltip, createRichContent, useAB } from '@insify/funnel-components';
import { makeContentContext } from '@objectiv/schema';
import { useVisibleEventTracker } from '@objectiv/tracker-react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useConditionsQuery, useParamsQuery } from '../../../../hooks/useQueries';

import { EmailSendOld } from './EmailSendOld';
import styles from './Overview.module.css';
import { Receipt } from './Receipt';

export const Overview = (): JSX.Element => {
  const { t } = useTranslation(['funnel']);
  const { data } = useParamsQuery();
  const { data: conditions } = useConditionsQuery();
  const ab = useAB();

  const riskCarriers = conditions?.riskCarriers;
  if (riskCarriers && riskCarriers.length !== 1) {
    Sentry.captureException('There should be exactly one risk carrier. Check the Airtable configuration', {
      extra: {
        riskCarriers,
      },
    });
  }
  const riskCarrier = riskCarriers?.[0];

  return (
    <>
      {conditions?.pastClaimsImpactsPremium ? (
        <OverviewHeadingPastClaimsImpactsPremium />
      ) : conditions?.foundingDateImpactsPremium ? (
        <OverviewHeadingFoundingDateImpactedPremium />
      ) : (
        <RichContentWithTooltip
          content={createRichContent(
            <>
              <RCHeading>{t('funnel:checkout_overview_heading')}</RCHeading>
              <RCParagraph>{t('funnel:checkout_overview_description')}</RCParagraph>
            </>,
          )}
          className={pageIntroContent}
        />
      )}
      <Receipt className={styles.receipt} country='FR' />
      {riskCarrier &&
        (ab.experiments.exampleContractDownloadShown === 'receipt' ? (
          <Trans
            className={clsx(textXs, styles.smallCopy)}
            i18nKey='checkout_overview_small_copy_new'
            values={{ riskCarrier: t(`funnel:risk_carrier_${riskCarrier}`) }}
            ns='funnel'
            parent='small'
          />
        ) : (
          <Trans
            className={clsx(textXs, styles.smallCopy)}
            i18nKey='checkout_overview_small_copy_old'
            values={{ riskCarrier: t(`funnel:risk_carrier_${riskCarrier}`) }}
            ns='funnel'
            components={{
              a: data?.representative?.email ? <EmailSendOld email={data.representative.email} /> : <></>,
            }}
            parent='small'
          />
        ))}
    </>
  );
};

const OverviewHeadingFoundingDateImpactedPremium = () => {
  const { t } = useTranslation(['funnel']);

  const trackVisibleEvent = useVisibleEventTracker({
    locationStack: [makeContentContext({ id: 'founding_date_impacted_premium' })],
  });

  useEffect(() => {
    void trackVisibleEvent();
  }, []);

  return (
    <RichContentWithTooltip
      content={createRichContent(
        <>
          <RCHeading>{t('funnel:checkout_overview_with_founding_date_impacted_premium_heading')}</RCHeading>
          <RCParagraph>{t('funnel:checkout_overview_with_founding_date_impacted_premium_description')}</RCParagraph>
        </>,
      )}
      className={pageIntroContent}
    />
  );
};

const OverviewHeadingPastClaimsImpactsPremium = () => {
  const { t } = useTranslation(['funnel']);

  const trackVisibleEvent = useVisibleEventTracker({
    locationStack: [makeContentContext({ id: 'past_claims_impacted_premium' })],
  });

  useEffect(() => {
    void trackVisibleEvent();
  }, []);

  return (
    <RichContentWithTooltip
      content={createRichContent(
        <>
          <RCHeading>{t('funnel:checkout_overview_with_past_claims_impacted_premium_heading')}</RCHeading>
          <RCParagraph>{t('funnel:checkout_overview_with_past_claims_impacted_premium_description')}</RCParagraph>
        </>,
      )}
      className={pageIntroContent}
    />
  );
};
